/**
 * User Subscription Management Page (CREG-10)
 *
 * Admin can view all user subscriptions and assign custom tiers to specific users.
 * Used for custom deals, enterprise agreements, or admin overrides.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Search,
  Loader2,
  UserCog,
  Crown,
  X,
  AlertCircle,
  ArrowLeft,
  Filter,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import Button from '../../../../components/base/Button';
import adminService, {
  type UserSubscription,
  type SubscriptionTier,
} from '../../../../services/adminService';

// ============================================================================
// ASSIGN TIER MODAL
// ============================================================================

function AssignTierModal({
  user,
  tiers,
  onAssign,
  onClose,
  isLoading,
}: {
  user: { id: string; email: string; firstName?: string; lastName?: string };
  tiers: SubscriptionTier[];
  onAssign: (userId: string, tierId: string, billingCycle: 'monthly' | 'yearly') => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [selectedTier, setSelectedTier] = useState<string>('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTier) {
      onAssign(user.id, selectedTier, billingCycle);
    }
  };

  const selectedTierData = tiers.find((t) => t.id === selectedTier);

  // Filter active tiers only
  const activeTiers = tiers.filter((t) => t.isActive);
  const brokerTiers = activeTiers.filter(
    (t) => t.planType === 'broker' || t.planType === 'agent'
  );
  const companyTiers = activeTiers.filter((t) => t.planType === 'company');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-bold text-[var(--pv-color-primary)] dark:text-white">
            Assign Tier to User
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* User Info */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <p className="text-sm text-gray-500">Assigning to:</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>

          {/* Tier Selection */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Select Tier
            </label>
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            >
              <option value="">Choose a tier...</option>
              {brokerTiers.length > 0 && (
                <optgroup label="Broker Plans">
                  {brokerTiers.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.name} - {tier.priceMonthly.toLocaleString()} SAR/mo
                    </option>
                  ))}
                </optgroup>
              )}
              {companyTiers.length > 0 && (
                <optgroup label="Company Plans">
                  {companyTiers.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.name} - {tier.priceMonthly.toLocaleString()} SAR/mo
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          {/* Billing Cycle */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Billing Cycle
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setBillingCycle('monthly')}
                className={`flex-1 py-2 px-4 rounded-xl border-2 transition-colors ${
                  billingCycle === 'monthly'
                    ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/30 text-[#003930] dark:text-[#B7DDD7]'
                    : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('yearly')}
                className={`flex-1 py-2 px-4 rounded-xl border-2 transition-colors ${
                  billingCycle === 'yearly'
                    ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/30 text-[#003930] dark:text-[#B7DDD7]'
                    : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                Yearly
              </button>
            </div>
          </div>

          {/* Price Preview */}
          {selectedTierData && (
            <div className="bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 rounded-xl p-4">
              <p className="text-sm text-[#003930] dark:text-[#B7DDD7]">
                <strong>{selectedTierData.name}</strong> -{' '}
                {billingCycle === 'yearly' ? 'Annual' : 'Monthly'}
              </p>
              <p className="text-2xl font-bold text-[#004D40] mt-1">
                {billingCycle === 'yearly'
                  ? selectedTierData.priceYearly.toLocaleString()
                  : selectedTierData.priceMonthly.toLocaleString()}{' '}
                SAR
                <span className="text-sm font-normal">
                  /{billingCycle === 'yearly' ? 'year' : 'month'}
                </span>
              </p>
            </div>
          )}

          {/* Warning */}
          <div className="flex items-start gap-2 text-amber-600 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              This will override any existing subscription for this user. No payment will be
              collected.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !selectedTier}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Assigning...
                </>
              ) : (
                <>
                  <Crown className="w-4 h-4 mr-2" />
                  Assign Tier
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AdminUserSubscriptionsPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [assigningUser, setAssigningUser] = useState<UserSubscription['user'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [subsResponse, tiersData] = await Promise.all([
        adminService.getUserSubscriptions({
          status: statusFilter || undefined,
        }),
        adminService.getTiers(),
      ]);
      setSubscriptions(subsResponse.data);
      setTiers(tiersData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load subscriptions');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter subscriptions by search query (client-side)
  const filteredSubscriptions = useMemo(() => {
    if (!debouncedSearch) return subscriptions;
    const query = debouncedSearch.toLowerCase();
    return subscriptions.filter(
      (sub) =>
        sub.user.email.toLowerCase().includes(query) ||
        sub.user.firstName?.toLowerCase().includes(query) ||
        sub.user.lastName?.toLowerCase().includes(query)
    );
  }, [subscriptions, debouncedSearch]);

  // Assign tier handler
  const handleAssignTier = async (
    userId: string,
    tierId: string,
    billingCycle: 'monthly' | 'yearly'
  ) => {
    try {
      setIsAssigning(true);
      setError(null);
      await adminService.assignTierToUser(userId, tierId, billingCycle);
      setAssigningUser(null);
      fetchData();
    } catch (err: unknown) {
      console.error('Failed to assign tier:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to assign tier';
      setError(errorMessage);
    } finally {
      setIsAssigning(false);
    }
  };

  // Status badge
  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      ACTIVE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      CANCELLED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      EXPIRED: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 dark:bg-gray-700'}`}
      >
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 p-8 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white">
                User Subscriptions
              </h1>
              <p className="text-gray-500">Manage user subscriptions and assign custom tiers</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email or name..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 rounded-xl border dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING">Pending</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>
        </div>

        {/* Subscriptions Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Billing
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Start Date
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-gray-700">
                {filteredSubscriptions.map((sub) => (
                  <tr
                    key={sub.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {sub.user.firstName} {sub.user.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{sub.user.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {sub.plan?.name || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                          {sub.plan?.planType}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-700 dark:text-gray-300">
                      {sub.billingCycle}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(sub.status)}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {format(new Date(sub.currentPeriodStart), 'MMM d, yyyy')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setAssigningUser(sub.user)}
                        className="p-2 hover:bg-[var(--pv-color-surface-sunken)] dark:hover:bg-[#004D40]/30 text-[#004D40] rounded-lg"
                        title="Assign different tier"
                      >
                        <UserCog className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredSubscriptions.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      {searchQuery || statusFilter
                        ? 'No subscriptions match your search'
                        : 'No subscriptions found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredSubscriptions.length} of {subscriptions.length} subscriptions
        </div>
      </div>

      {/* Assign Tier Modal */}
      {assigningUser && (
        <AssignTierModal
          user={assigningUser}
          tiers={tiers}
          onAssign={handleAssignTier}
          onClose={() => setAssigningUser(null)}
          isLoading={isAssigning}
        />
      )}
    </div>
  );
}
